// Share Modal — shows share link with copy-to-clipboard
import { useState, useEffect } from 'react';
import { projectsApi } from '@/api/mapApi';
import { ApiError } from '@/api/client';

interface ShareModalProps {
  projectId: string;
  projectName: string;
  onClose: () => void;
}

export function ShareModal({ projectId, projectName, onClose }: ShareModalProps) {
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const res = await projectsApi.share(projectId);
      setShareUrl(res.share_url);
    } catch (err) {
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function handleShare() {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: projectName, url: shareUrl });
      } catch {}
    } else {
      handleCopy();
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">Chia sẻ đồ án</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Gửi link dưới đây để người khác xem đồ án <strong>{projectName}</strong>:
        </p>
        {loading && <p className="text-sm text-gray-500">Đang tạo link...</p>}
        {shareUrl && (
          <>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 border border-gray-300 rounded text-xs font-mono"
              />
              <button
                onClick={handleCopy}
                className="px-3 py-2 bg-brand-600 text-white text-sm rounded hover:bg-brand-700 whitespace-nowrap"
              >
                {copied ? '✓ Đã sao' : 'Sao chép'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              ⚠️ Link có thể được dùng để truy cập. Bạn có thể revoke bằng cách tạo link mới.
            </p>
            <button
              onClick={handleShare}
              className="w-full mt-3 px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
            >
              📤 Chia sẻ qua...
            </button>
          </>
        )}
        <button
          onClick={onClose}
          className="w-full mt-4 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
        >
          Đóng
        </button>
      </div>
    </div>
  );
}
